/**
 * Trovva — Full Onboarding Flow
 *
 * Full-screen, one-step-at-a-time onboarding wizard that covers every setup
 * item a broker needs before Trovva is fully operational.
 *
 * Steps:
 *   0. Welcome — warm greeting, reassurance
 *   1. Brokerage Info — name, address, phone, license
 *   2. Logo & Branding — logo upload, brand color
 *   3. Broker Profile — name, cell, license number
 *   4. Connect Email — Gmail or Outlook OAuth
 *   5. Invite Agents — team emails (skippable)
 *   6. Lead Sources — Zillow / Facebook / manual (skippable)
 *   7. Caller ID — STIR/SHAKEN setup (skippable)
 *   8. Documents & E-Sign — NJAR forms + BoldSign (skippable)
 *   9. Billing — Stripe checkout or start trial
 *  10. You're All Set! — celebration + next actions
 *
 * Features:
 *   - Exit button on every step — saves progress, hides for this session
 *   - Resume on return — detects saved progress, shows "welcome back"
 *   - Brain chat bubble at every step for help
 *   - Progress bar with step count and percentage
 *   - DB-backed persistence via onboarding_progress JSONB
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowRight, ArrowLeft, Check, Building2, User, Users, UserPlus,
  Sparkles, Loader2, MessageSquare, X, Send, ChevronRight, LogOut,
  Phone, Mail, MapPin, Shield, Zap, Palette, FileText, CreditCard,
  Upload, Globe, PhoneCall, PenTool, ExternalLink, Image,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const TOTAL_STEPS = 11;
const SETUP_STEPS = TOTAL_STEPS - 2; // Exclude welcome and all-set

const STEP_META = [
  { title: 'Welcome to Trovva', icon: Sparkles, color: '#7C3AED' },
  { title: 'Your Brokerage', icon: Building2, color: '#1B65A6' },
  { title: 'Logo & Branding', icon: Palette, color: '#E11D48' },
  { title: 'Your Profile', icon: User, color: '#0EA5E9' },
  { title: 'Connect Email', icon: Mail, color: '#7C3AED' },
  { title: 'Invite Your Team', icon: Users, color: '#10B981' },
  { title: 'Lead Sources', icon: Globe, color: '#F59E0B' },
  { title: 'Caller ID Setup', icon: PhoneCall, color: '#6366F1' },
  { title: 'Documents & E-Sign', icon: FileText, color: '#0D9488' },
  { title: 'Billing', icon: CreditCard, color: '#1B65A6' },
  { title: 'You\'re All Set!', icon: Sparkles, color: '#7C3AED' },
];

// ── Styles ──────────────────────────────────────────────────
const cardStyle = {
  background: '#fff', borderRadius: 16, padding: 32,
  maxWidth: 540, width: '100%', boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
};
const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 10,
  border: `1px solid ${theme.colors.border}`, fontSize: 14,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: theme.colors.textLight, marginBottom: 4 };
const primaryBtn = (disabled) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '12px 28px', borderRadius: 10, border: 'none',
  background: theme.colors.primary, color: '#fff',
  fontSize: 15, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1, transition: 'all 0.2s',
});
const ghostBtn = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '10px 16px', borderRadius: 10, border: 'none',
  background: 'transparent', color: theme.colors.textLight,
  fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
const oauthBtnStyle = (bg) => ({
  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
  padding: '14px 20px', borderRadius: 12, border: `1px solid ${theme.colors.border}`,
  background: bg || '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  transition: 'box-shadow 0.15s',
});
const skipBtn = { ...ghostBtn, color: theme.colors.primary };
const setupCardStyle = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
  borderRadius: 12, border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
  transition: 'all 0.15s',
};

export default function OnboardingFlow({ user, onComplete, onExit }) {
  const [step, setStep] = useState(0);
  const [isReturning, setIsReturning] = useState(false);
  const [returnStep, setReturnStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBrainChat, setShowBrainChat] = useState(false);

  // Form data
  const [brokerage, setBrokerage] = useState({ name: '', phone: '', address: '', city: '', state: 'NJ', zip: '', license: '' });
  const [branding, setBranding] = useState({ brand_color: '#1B65A6', logo_url: '' });
  const [profile, setProfile] = useState({ full_name: '', phone: '', license_number: '' });
  const [teamEmails, setTeamEmails] = useState(['']);
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailProvider, setEmailProvider] = useState(null);
  const [stepsCompleted, setStepsCompleted] = useState({});

  const userName = user?.user_metadata?.full_name?.split(' ')[0] || profile.full_name?.split(' ')[0] || 'there';

  // Load saved progress
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setLoading(false); return; }
        const resp = await fetch('/api/settings?section=onboarding_status', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          const progress = data.onboarding_progress;
          if (progress) {
            if (progress.completed) { onComplete?.(); return; }
            if (progress.step > 0 && progress.step < TOTAL_STEPS - 1) {
              setReturnStep(progress.step);
              setIsReturning(true);
              // Stay on step 0 (welcome back) — user clicks Continue to resume
            }
            if (progress.brokerage) setBrokerage(prev => ({ ...prev, ...progress.brokerage }));
            if (progress.branding) setBranding(prev => ({ ...prev, ...progress.branding }));
            if (progress.profile) setProfile(prev => ({ ...prev, ...progress.profile }));
            if (progress.stepsCompleted) setStepsCompleted(progress.stepsCompleted);
            if (progress.emailConnected) setEmailConnected(true);
          }
        }
      } catch (e) { /* continue with fresh start */ }
      setLoading(false);
    })();
  }, []);

  // Save progress
  const saveProgress = useCallback(async (currentStep, extraData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch('/api/settings?section=onboarding_progress', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: currentStep,
          brokerage,
          branding,
          profile,
          stepsCompleted,
          emailConnected,
          completed: currentStep >= TOTAL_STEPS - 1,
          ...extraData,
        }),
      });
    } catch (e) { /* silent */ }
  }, [brokerage, branding, profile, stepsCompleted, emailConnected]);

  // Exit handler — save progress and hide for this session
  const handleExit = async () => {
    await saveProgress(step, { dismissed: true });
    onExit?.();
  };

  const goNext = async () => {
    setSaving(true);
    const nextStep = step + 1;

    // Step-specific saves
    if (step === 1 && brokerage.name) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch('/api/settings?section=org', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(brokerage),
          });
          setStepsCompleted(prev => ({ ...prev, brokerage: true }));
        }
      } catch (e) { /* continue */ }
    }

    if (step === 2) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch('/api/settings?section=org', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(branding),
          });
          setStepsCompleted(prev => ({ ...prev, branding: true }));
        }
      } catch (e) { /* continue */ }
    }

    if (step === 3 && profile.full_name) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch('/api/settings?section=profile', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(profile),
          });
          setStepsCompleted(prev => ({ ...prev, profile: true }));
        }
      } catch (e) { /* continue */ }
    }

    if (step === 5) {
      const validEmails = teamEmails.filter(e => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
      if (validEmails.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            for (const email of validEmails) {
              await fetch('/api/admin-team', {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), role: 'agent' }),
              }).catch(() => {});
            }
            setStepsCompleted(prev => ({ ...prev, team: true }));
          }
        } catch (e) { /* continue */ }
      }
    }

    await saveProgress(nextStep);

    if (nextStep >= TOTAL_STEPS - 1) {
      await saveProgress(nextStep, { completed: true });
    }

    setStep(nextStep);
    setIsReturning(false);
    setSaving(false);
  };

  const goBack = () => { if (step > 0) setStep(step - 1); };
  const skip = async () => { const next = step + 1; setStep(next); await saveProgress(next); };
  const finish = () => { onComplete?.(); };

  if (loading) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.colors.background }}>
        <Loader2 size={28} style={{ color: theme.colors.primary, animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  const meta = STEP_META[step] || STEP_META[0];
  const Icon = meta.icon;
  const progress = step === 0 ? 0 : step >= TOTAL_STEPS - 1 ? 100 : Math.round((step / SETUP_STEPS) * 100);

  // Navigation footer shared by most steps
  const NavFooter = ({ canNext = true, nextLabel = 'Next', showSkip = false, showBack = true, onNext, onSkip }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28 }}>
      {showBack && step > 0 ? (
        <button onClick={goBack} style={ghostBtn}><ArrowLeft size={16} /> Back</button>
      ) : <div />}
      <div style={{ display: 'flex', gap: 8 }}>
        {showSkip && (
          <button onClick={onSkip || skip} style={skipBtn}>Skip for now</button>
        )}
        <button onClick={onNext || goNext} disabled={saving || !canNext} style={primaryBtn(saving || !canNext)}>
          {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
          {nextLabel} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(180deg, ${theme.colors.background} 0%, #E8F0FE 100%)`,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: '24px 16px', overflow: 'auto',
    }}>
      {/* Exit button — always visible except on welcome and final step */}
      {step > 0 && step < TOTAL_STEPS - 1 && (
        <button onClick={handleExit} style={{
          position: 'fixed', top: 20, right: 24, zIndex: 10000,
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`,
          background: '#fff', color: theme.colors.textLight, fontSize: 13, fontWeight: 500,
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <LogOut size={14} /> Finish later
        </button>
      )}

      {/* Progress bar */}
      {step > 0 && step < TOTAL_STEPS - 1 && (
        <div style={{ width: '100%', maxWidth: 540, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>
              Step {step} of {SETUP_STEPS}
            </span>
            <span style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 600 }}>{progress}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: '#E2E8F0', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.accent})`,
              width: `${progress}%`, transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      )}

      {/* ── Step Card ── */}
      <div style={cardStyle}>
        {/* Step icon */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: `${meta.color}15`, marginBottom: 12,
          }}>
            <Icon size={28} style={{ color: meta.color }} />
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 0: Welcome                                        */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            {isReturning ? (
              <>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, marginBottom: 10 }}>
                  Hey {userName}, welcome back!
                </h1>
                <p style={{ fontSize: 15, color: theme.colors.textLight, lineHeight: 1.7, marginBottom: 8 }}>
                  Great to see you again! You were on step {returnStep} of {SETUP_STEPS} last time. Let's pick up right where you left off.
                </p>
                <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 28 }}>
                  It only takes a few more minutes to get everything set up.
                </p>
                <button onClick={() => { setStep(returnStep); setIsReturning(false); }} style={primaryBtn(false)}>
                  Continue Setup <ArrowRight size={18} />
                </button>
                <div style={{ marginTop: 12 }}>
                  <button onClick={() => { setStep(1); setIsReturning(false); }} style={{ ...ghostBtn, justifyContent: 'center', width: '100%' }}>
                    Start over from the beginning
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, marginBottom: 10 }}>
                  Welcome to Trovva, {userName}!
                </h1>
                <p style={{ fontSize: 15, color: theme.colors.textLight, lineHeight: 1.7, marginBottom: 8 }}>
                  We're going to get your brokerage fully set up in just a few minutes. It's totally okay if this feels like a lot right now — that's normal with any new system.
                </p>
                <p style={{ fontSize: 15, color: theme.colors.textLight, lineHeight: 1.7, marginBottom: 8 }}>
                  Think of me as your co-pilot. I'll walk you through everything one step at a time, and if you ever get stuck, just tap <strong>"Ask Trovva"</strong> — I'm always here.
                </p>
                <p style={{ fontSize: 14, color: theme.colors.primary, fontWeight: 500, lineHeight: 1.6, marginBottom: 28 }}>
                  You can leave anytime and come back — I'll remember exactly where you were.
                </p>
                <button onClick={goNext} style={primaryBtn(false)}>
                  Let's Go <ArrowRight size={18} />
                </button>
              </>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 1: Brokerage Info                                 */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Tell me about your brokerage</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              This is how your brokerage appears to agents, clients, and on all outbound communications. You can always change it later in Settings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Brokerage Name *</label>
                <input value={brokerage.name} onChange={e => setBrokerage(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Sabal Real Estate" style={inputStyle} autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Office Phone</label>
                  <input value={brokerage.phone} onChange={e => setBrokerage(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(201) 555-0100" type="tel" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Broker License #</label>
                  <input value={brokerage.license} onChange={e => setBrokerage(p => ({ ...p, license: e.target.value }))}
                    placeholder="e.g. 0123456" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Street Address</label>
                <input value={brokerage.address} onChange={e => setBrokerage(p => ({ ...p, address: e.target.value }))}
                  placeholder="123 Main St, Suite 200" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: 12 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input value={brokerage.city} onChange={e => setBrokerage(p => ({ ...p, city: e.target.value }))}
                    placeholder="Montclair" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <input value={brokerage.state} onChange={e => setBrokerage(p => ({ ...p, state: e.target.value }))}
                    placeholder="NJ" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>ZIP</label>
                  <input value={brokerage.zip} onChange={e => setBrokerage(p => ({ ...p, zip: e.target.value }))}
                    placeholder="07042" style={inputStyle} />
                </div>
              </div>
            </div>
            <NavFooter canNext={!!brokerage.name.trim()} showBack={false} />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 2: Logo & Branding                                */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Make it yours</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              Your logo and brand color appear on emails, the client portal, agent bio pages, and documents. This makes everything look professional from day one.
            </p>

            {/* Logo upload */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Brokerage Logo</label>
              <div style={{
                border: `2px dashed ${theme.colors.border}`, borderRadius: 12,
                padding: 24, textAlign: 'center', cursor: 'pointer',
                background: branding.logo_url ? '#F8FAFC' : '#fff',
                transition: 'border-color 0.2s',
              }}
                onClick={() => document.getElementById('logo-upload')?.click()}
              >
                {branding.logo_url ? (
                  <div>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
                    <div style={{ fontSize: 13, color: theme.colors.primary, fontWeight: 600 }}>Logo uploaded</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>Click to change</div>
                  </div>
                ) : (
                  <div>
                    <Upload size={28} style={{ color: theme.colors.textLight, marginBottom: 8 }} />
                    <div style={{ fontSize: 13, color: theme.colors.text, fontWeight: 500 }}>Click to upload your logo</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>PNG, JPG, or SVG — recommended 400x100px</div>
                  </div>
                )}
                <input id="logo-upload" type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session) return;
                      const ext = file.name.split('.').pop();
                      const path = `logos/${user?.id || 'org'}/logo.${ext}`;
                      const { error } = await supabase.storage.from('assets').upload(path, file, { upsert: true });
                      if (!error) {
                        const { data: urlData } = supabase.storage.from('assets').getPublicUrl(path);
                        setBranding(p => ({ ...p, logo_url: urlData.publicUrl }));
                      }
                    } catch (err) { /* silent */ }
                  }}
                />
              </div>
            </div>

            {/* Brand color */}
            <div>
              <label style={labelStyle}>Brand Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="color" value={branding.brand_color} onChange={e => setBranding(p => ({ ...p, brand_color: e.target.value }))}
                  style={{ width: 48, height: 42, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0 }} />
                <input value={branding.brand_color} onChange={e => setBranding(p => ({ ...p, brand_color: e.target.value }))}
                  style={{ ...inputStyle, width: 120, fontFamily: 'monospace' }} />
                <div style={{ display: 'flex', gap: 6 }}>
                  {['#1B65A6', '#10B981', '#7C3AED', '#E11D48', '#F59E0B', '#0EA5E9'].map(c => (
                    <button key={c} onClick={() => setBranding(p => ({ ...p, brand_color: c }))} style={{
                      width: 28, height: 28, borderRadius: 8, border: branding.brand_color === c ? '2px solid #333' : '2px solid transparent',
                      background: c, cursor: 'pointer', padding: 0,
                    }} />
                  ))}
                </div>
              </div>
            </div>

            <NavFooter showSkip canNext />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 3: Broker Profile                                 */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Now let's set up you</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              Your agents and clients will see this info. It's also used for compliance records and document signing.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Your Full Name *</label>
                <input value={profile.full_name || user?.user_metadata?.full_name || ''} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Jennifer Raffa" style={inputStyle} autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Cell Phone</label>
                  <input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(201) 555-0101" type="tel" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>NJ License #</label>
                  <input value={profile.license_number} onChange={e => setProfile(p => ({ ...p, license_number: e.target.value }))}
                    placeholder="1234567" style={inputStyle} />
                </div>
              </div>
            </div>
            <NavFooter canNext />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 4: Connect Email                                  */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 4 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Connect your email</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 6 }}>
              Trovva syncs with your inbox to automatically capture leads from inquiries, send follow-ups, and run drip email campaigns.
            </p>
            <p style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 20 }}>
              We support Gmail and Microsoft Outlook/365. You can connect both if you use both.
            </p>

            {emailConnected ? (
              <div style={{
                padding: 20, borderRadius: 12, background: '#F0FDF4',
                border: '1px solid #BBF7D0', textAlign: 'center', marginBottom: 8,
              }}>
                <Check size={28} style={{ color: '#16A34A', marginBottom: 8 }} />
                <div style={{ fontSize: 15, fontWeight: 600, color: '#15803D' }}>
                  Email connected!
                </div>
                <div style={{ fontSize: 13, color: '#16A34A', marginTop: 4 }}>
                  {emailProvider === 'google' ? 'Gmail' : 'Microsoft Outlook'} is syncing
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session) {
                      const r = await fetch('/api/gmail-auth', { headers: { Authorization: `Bearer ${session.access_token}` } });
                      const d = await r.json();
                      if (d.url) { window.open(d.url, '_blank', 'width=500,height=600'); setEmailProvider('google'); }
                    }
                  } catch { /* silent */ }
                }} style={oauthBtnStyle()}>
                  <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Connect Gmail
                </button>

                <button onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session) {
                      const r = await fetch('/api/microsoft-auth', { headers: { Authorization: `Bearer ${session.access_token}` } });
                      const d = await r.json();
                      if (d.url) { window.open(d.url, '_blank', 'width=500,height=600'); setEmailProvider('microsoft'); }
                    }
                  } catch { /* silent */ }
                }} style={oauthBtnStyle()}>
                  <svg width="20" height="20" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                  Connect Microsoft Outlook
                </button>
              </div>
            )}

            <NavFooter showSkip canNext nextLabel={emailConnected ? 'Next' : 'Next'} />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 5: Invite Agents                                  */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 5 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Invite your agents</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 6 }}>
              Add your team members' email addresses. They'll get an invite to join your brokerage on Trovva.
            </p>
            <p style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 20 }}>
              Solo broker? Skip this for now and add agents later from Settings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {teamEmails.map((email, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={email} onChange={e => {
                    const newEmails = [...teamEmails];
                    newEmails[i] = e.target.value;
                    setTeamEmails(newEmails);
                  }} placeholder={`agent${i + 1}@email.com`} type="email" style={{ ...inputStyle, flex: 1 }} />
                  {teamEmails.length > 1 && (
                    <button onClick={() => setTeamEmails(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, padding: 4 }}>
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              {teamEmails.length < 10 && (
                <button onClick={() => setTeamEmails(prev => [...prev, ''])} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                  background: 'none', border: `1px dashed ${theme.colors.border}`, borderRadius: 8,
                  color: theme.colors.primary, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}>
                  + Add another agent
                </button>
              )}
            </div>
            <NavFooter showSkip canNext nextLabel="Send Invites" />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 6: Lead Sources                                   */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 6 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Where do your leads come from?</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              Connect your lead sources so inquiries flow straight into your CRM with instant auto-response. You can add more anytime from Settings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { icon: '🏠', name: 'Zillow', desc: 'Tech Connect API — free partnership, leads arrive in seconds', key: 'zillow' },
                { icon: '📘', name: 'Facebook Lead Ads', desc: 'Webhook integration — leads from FB/IG ad forms', key: 'facebook' },
                { icon: '✉️', name: 'Email Parsing', desc: 'Auto-capture leads from inquiry emails in your inbox', key: 'email', auto: true },
                { icon: '🌐', name: 'Website (Trovva Pixel)', desc: 'Track visitors on your brokerage website', key: 'pixel' },
                { icon: '📱', name: 'Social Media Bio', desc: 'Link-in-bio page for agent social profiles', key: 'bio', auto: true },
              ].map(source => (
                <div key={source.key} style={{
                  ...setupCardStyle,
                  background: source.auto ? '#F0FDF4' : '#fff',
                  borderColor: source.auto ? '#BBF7D0' : theme.colors.border,
                }}
                  onMouseEnter={e => { if (!source.auto) e.currentTarget.style.borderColor = theme.colors.primary; }}
                  onMouseLeave={e => { if (!source.auto) e.currentTarget.style.borderColor = theme.colors.border; }}
                >
                  <span style={{ fontSize: 24 }}>{source.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{source.name}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight }}>{source.desc}</div>
                  </div>
                  {source.auto ? (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#16A34A', background: '#DCFCE7', padding: '3px 8px', borderRadius: 6 }}>Auto</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 600, color: theme.colors.primary, background: `${theme.colors.primary}12`, padding: '3px 8px', borderRadius: 6 }}>Settings</span>
                  )}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 14, lineHeight: 1.5 }}>
              Email parsing and bio pages are available automatically once you connect your email. The others can be configured in Settings → Integrations.
            </p>
            <NavFooter showSkip canNext />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 7: Caller ID / STIR/SHAKEN                        */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 7 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Caller ID verification</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 8 }}>
              Without caller ID verification, your agents' outbound calls may show as "Spam Likely" on the recipient's phone — and nobody picks up a spam call.
            </p>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              STIR/SHAKEN is a free FCC-mandated protocol that verifies your business phone number. Trovva handles this through Twilio Trust Hub — it takes about 5 minutes.
            </p>

            <div style={{
              padding: 16, borderRadius: 12, background: '#FFFBEB',
              border: '1px solid #FDE68A', marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Shield size={18} style={{ color: '#D97706' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#92400E' }}>What you'll need</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#78350F', lineHeight: 1.8 }}>
                <li>Your brokerage EIN (tax ID)</li>
                <li>Business address (same as above)</li>
                <li>Authorized representative name</li>
                <li>Business phone number to verify</li>
              </ul>
            </div>

            <p style={{ fontSize: 12, color: theme.colors.textLight, lineHeight: 1.5 }}>
              You can set this up now or later from Settings → Voice & Calling. If you skip it, outbound calls will still work but may be flagged as unverified.
            </p>

            <NavFooter showSkip canNext nextLabel="Set Up in Settings" onNext={async () => {
              setStepsCompleted(prev => ({ ...prev, callerid_seen: true }));
              await skip();
            }} />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 8: Documents & E-Signature                        */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 8 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Documents & e-signatures</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              Trovva can auto-fill NJ real estate forms from deal data and send them for electronic signature — no more printing, scanning, or chasing paper.
            </p>

            {/* NJAR Forms */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FileText size={18} style={{ color: '#0D9488' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>NJAR Form Templates</span>
              </div>
              <p style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 12 }}>
                NJAR forms are copyrighted — each brokerage must upload their own licensed copies. You'll need the standard contract of sale, disclosure forms, and any addenda you commonly use.
              </p>
              <div style={{
                border: `2px dashed ${theme.colors.border}`, borderRadius: 12,
                padding: 20, textAlign: 'center', cursor: 'pointer',
              }}
                onClick={() => document.getElementById('njar-upload')?.click()}
              >
                <Upload size={24} style={{ color: theme.colors.textLight, marginBottom: 6 }} />
                <div style={{ fontSize: 13, color: theme.colors.text, fontWeight: 500 }}>Upload NJAR PDFs</div>
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>PDF files only</div>
                <input id="njar-upload" type="file" accept=".pdf" multiple style={{ display: 'none' }} />
              </div>
            </div>

            {/* BoldSign */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <PenTool size={18} style={{ color: '#0D9488' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>E-Signature (BoldSign)</span>
              </div>
              <p style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 8 }}>
                Trovva includes 10 e-sign envelopes per seat per month. E-signature is pre-configured — you can start sending documents for signing right away from any deal.
              </p>
              <div style={{
                padding: 12, borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Check size={16} style={{ color: '#16A34A' }} />
                <span style={{ fontSize: 13, color: '#15803D', fontWeight: 500 }}>E-signature is ready to use — no setup needed</span>
              </div>
            </div>

            <NavFooter showSkip canNext />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 9: Billing                                        */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === 9 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>Almost there — billing</h2>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 20 }}>
              You're on a free 14-day trial right now — everything is unlocked. Add a payment method anytime before the trial ends.
            </p>

            <div style={{
              padding: 20, borderRadius: 12, background: `linear-gradient(135deg, ${theme.colors.primary}08, ${theme.colors.accent}08)`,
              border: `1px solid ${theme.colors.primary}20`, marginBottom: 20,
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 800, color: theme.colors.primary, marginBottom: 4 }}>$79</div>
                <div style={{ fontSize: 14, color: theme.colors.textLight, fontWeight: 500 }}>per seat / month</div>
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 8, lineHeight: 1.6 }}>
                  Includes: CRM, Trovva Brain, 60 voice minutes, 10 e-sign envelopes, email sync, lead capture, and everything you see in Trovva.
                </div>
              </div>

              <div style={{
                marginTop: 16, padding: 12, borderRadius: 10, background: '#fff',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                {[
                  'Unlimited leads & contacts',
                  'Trovva Brain AI',
                  '60 voice minutes / seat / month',
                  '10 e-sign envelopes / seat / month',
                  'Email sync & drip campaigns',
                  'Client portal with magic links',
                  'Compliance tracking',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.colors.text }}>
                    <Check size={14} style={{ color: '#16A34A', flexShrink: 0 }} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: 12, borderRadius: 10, background: '#EFF6FF',
              border: '1px solid #BFDBFE', textAlign: 'center', marginBottom: 8,
            }}>
              <span style={{ fontSize: 13, color: '#1E40AF', fontWeight: 500 }}>
                🎉 Your 14-day free trial is active — no credit card needed yet
              </span>
            </div>

            <NavFooter canNext nextLabel="Continue" showSkip={false} />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* STEP 10: All Set!                                      */}
        {/* ═══════════════════════════════════════════════════════ */}
        {step === TOTAL_STEPS - 1 && (
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text, marginBottom: 10 }}>
              You're all set, {userName}!
            </h1>
            <p style={{ fontSize: 15, color: theme.colors.textLight, lineHeight: 1.7, marginBottom: 8 }}>
              Your brokerage is live on Trovva. Everything is configured and ready to go.
            </p>
            <p style={{ fontSize: 14, color: theme.colors.textLight, lineHeight: 1.6, marginBottom: 24 }}>
              Explore at your own pace — and remember, I'm always one click away if you need me. Just hit <strong>Ask Trovva</strong> anytime.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', marginBottom: 28 }}>
              {[
                { icon: Users, label: 'Check your CRM', desc: 'See your leads pipeline and start managing contacts', color: '#1B65A6' },
                { icon: Mail, label: 'Send your first email', desc: 'Try the AI follow-up writer on any contact', color: '#7C3AED' },
                { icon: FileText, label: 'Create a deal', desc: 'Track a transaction and send docs for signing', color: '#10B981' },
                { icon: Shield, label: 'Review compliance', desc: 'Check your broker file and NJ requirements', color: '#0D9488' },
              ].map((item, i) => (
                <div key={i} style={{
                  ...setupCardStyle,
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${item.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.icon size={18} style={{ color: item.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight }}>{item.desc}</div>
                  </div>
                  <ChevronRight size={16} style={{ color: theme.colors.textLight }} />
                </div>
              ))}
            </div>

            <button onClick={finish} style={{ ...primaryBtn(false), width: '100%', justifyContent: 'center' }}>
              Go to Dashboard <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* ── Brain Chat Bubble ── */}
      {step > 0 && step < TOTAL_STEPS - 1 && (
        <BrainChatBubble show={showBrainChat} onToggle={() => setShowBrainChat(!showBrainChat)} step={step} />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
      `}</style>
    </div>
  );
}

// ── Brain Chat Bubble (always available during onboarding) ──
function BrainChatBubble({ show, onToggle, step }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const contextHints = {
    1: 'setting up their brokerage details',
    2: 'uploading their logo and choosing a brand color',
    3: 'filling in their personal profile',
    4: 'connecting their email (Gmail or Outlook)',
    5: 'inviting their team members',
    6: 'learning about lead source integrations',
    7: 'setting up caller ID verification (STIR/SHAKEN)',
    8: 'learning about document management and e-signatures',
    9: 'reviewing billing and their free trial',
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!message.trim() || sending) return;
    const userMsg = message.trim();
    setMessage('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const resp = await fetch('/api/brain', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          context: `User is going through onboarding step ${step} of ${SETUP_STEPS}, currently ${contextHints[step] || 'setting up'}. Help them with their question in a warm, encouraging tone. Keep answers short and actionable.`,
        }),
      });
      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.reply || data.response || 'I\'m here to help! What would you like to know?' }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I couldn\'t connect right now. But don\'t worry — you can always reach me from the dashboard!' }]);
    }
    setSending(false);
  };

  return (
    <>
      <button onClick={onToggle} style={{
        position: 'fixed', bottom: 24, right: 24,
        width: 56, height: 56, borderRadius: '50%',
        background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
        border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 10001,
        animation: messages.length === 0 ? 'pulse 2s ease-in-out infinite' : 'none',
      }}>
        {show ? <X size={24} color="#fff" /> : <MessageSquare size={24} color="#fff" />}
      </button>

      {!show && messages.length === 0 && (
        <div onClick={onToggle} style={{
          position: 'fixed', bottom: 34, right: 88,
          background: '#fff', borderRadius: 10, padding: '8px 14px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)', cursor: 'pointer', zIndex: 10001,
          fontSize: 13, fontWeight: 500, color: theme.colors.text,
          animation: 'fadeIn 0.5s ease 1s both',
        }}>
          Stuck? Ask Trovva!
        </div>
      )}

      {show && (
        <div style={{
          position: 'fixed', bottom: 90, right: 24,
          width: 340, maxHeight: 400, borderRadius: 16,
          background: '#fff', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', zIndex: 10001,
          animation: 'fadeIn 0.2s ease', overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px', background: theme.colors.primary, color: '#fff', fontWeight: 600, fontSize: 14 }}>
            Ask Trovva
          </div>
          <div style={{ flex: 1, padding: 12, overflowY: 'auto', maxHeight: 280 }}>
            {messages.length === 0 && (
              <div style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.6, padding: 8 }}>
                I'm Trovva Brain, your setup guide. Ask me anything — "What should I do first?", "How do I invite my agents?", "What is STIR/SHAKEN?", or "Can I skip this step?"
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{
                  maxWidth: '80%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                  background: m.role === 'user' ? theme.colors.primary : '#F1F5F9',
                  color: m.role === 'user' ? '#fff' : theme.colors.text,
                }}>{m.text}</div>
              </div>
            ))}
            {sending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={{ padding: '8px 12px', borderRadius: 12, background: '#F1F5F9', fontSize: 13, color: theme.colors.textLight }}>
                  Thinking...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div style={{ padding: '8px 12px', borderTop: `1px solid ${theme.colors.border}`, display: 'flex', gap: 8 }}>
            <input value={message} onChange={e => setMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask me anything..." style={{ ...inputStyle, flex: 1, padding: '8px 12px', fontSize: 13 }} />
            <button onClick={sendMessage} disabled={sending || !message.trim()} style={{
              background: theme.colors.primary, border: 'none', borderRadius: 8, padding: '8px 12px',
              cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center',
              opacity: sending || !message.trim() ? 0.5 : 1,
            }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
